import React, { useState, useEffect, useRef } from 'react';
import { Users, Send, Radio, Lock, LogOut, RefreshCw, Power, Image as ImageIcon, Search, Paperclip, X, Star } from 'lucide-react';

// TAVS REĀLAIS DOMĒNS (AI Backend serveris)
const API_URL = '/api'; 
const UPLOADS_URL = '/uploads';

function AppAdmin() {
  const [password, setPassword] = useState(localStorage.getItem('adminKey') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('adminKey'));
  
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('chat'); // 'chat' or 'dashboard'
  
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const getHeaders = () => ({ 'X-Admin-Pass': password });

  const handleLogin = (e) => {
    e.preventDefault();
    localStorage.setItem('adminKey', password);
    setIsLoggedIn(true);
  };
  
  const logout = () => {
    localStorage.removeItem('adminKey');
    setIsLoggedIn(false);
    setPassword('');
  };

  const scrollToBottom = () => {
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      console.log("Fetching users from:", `${API_URL}/users`);
      const res = await fetch(`${API_URL}/users`, { 
        headers: { 
          ...getHeaders(), 
          'Content-Type': 'application/json' 
        } 
      });
      if (res.status === 401) return logout();
      if (!res.ok) throw new Error(`Servera kļūda: ${res.status}`);
      const data = await res.json();
      console.log("Users fetched:", data.length);
      setUsers(data);
      setFilteredUsers(data);
    } catch (err) { 
      console.error("Fetch Users Error:", err);
    } 
    finally { setLoading(false); }
  };

  const fetchHistory = async (userId, shouldScroll = false) => {
    try {
      const res = await fetch(`${API_URL}/history?user_id=${userId}`, { 
        headers: { 
            ...getHeaders(), 
            'Content-Type': 'application/json' 
        } 
      });
      const data = await res.json();
      setChatHistory(data);
      if (shouldScroll) scrollToBottom();
    } catch (err) { console.error(err); }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    fetchHistory(user.user_id, true);
    setMessage('');
    setSelectedFile(null);
  };

  const handleSearch = (e) => {
      const term = e.target.value.toLowerCase();
      setSearchTerm(term);
      const filtered = users.filter(u => 
          (u.first_name && u.first_name.toLowerCase().includes(term)) ||
          (u.username && u.username.toLowerCase().includes(term)) ||
          (u.email && u.email.toLowerCase().includes(term)) ||
          String(u.user_id).includes(term)
      );
      setFilteredUsers(filtered);
  };

  const toggleIntervention = async () => {
    if (!selectedUser) return;
    const newMode = selectedUser.intervention_mode === 1 ? 0 : 1;
    const updatedUser = { ...selectedUser, intervention_mode: newMode };
    setSelectedUser(updatedUser);
    setUsers(users.map(u => u.user_id === selectedUser.user_id ? updatedUser : u));

    try {
        await fetch(`${API_URL}/toggle_intervention`, {
            method: 'POST',
            headers: { 'X-Admin-Pass': password, 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: selectedUser.user_id, mode: newMode })
        });
    } catch (e) { alert("Kļūda mainot režīmu"); }
  };

  const toggleVip = async () => {
    if (!selectedUser) return;
    const newVip = selectedUser.is_vip === 1 ? 0 : 1;
    const updatedUser = { ...selectedUser, is_vip: newVip };
    setSelectedUser(updatedUser);
    setUsers(users.map(u => u.user_id === selectedUser.user_id ? updatedUser : u));

    try {
        await fetch(`${API_URL}/toggle_vip`, {
            method: 'POST',
            headers: { 'X-Admin-Pass': password, 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: selectedUser.user_id, is_vip: newVip })
        });
    } catch (e) { alert("Kļūda mainot VIP statusu"); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!message.trim() && !selectedFile) || !selectedUser) return;
    
    const newMsg = { role: 'admin', content: selectedFile ? `[Nosūtīts fails: ${selectedFile.name}] ${message}` : message, timestamp: new Date(), msg_type: 'text' };
    setChatHistory([...chatHistory, newMsg]);
    const currentMsg = message;
    const currentFile = selectedFile;
    setMessage('');
    setSelectedFile(null);
    scrollToBottom();

    try {
      const formData = new FormData();
      formData.append('user_id', selectedUser.user_id);
      formData.append('text', currentMsg);
      if (currentFile) formData.append('file', currentFile);
      
      await fetch(`${API_URL}/reply`, { method: 'POST', headers: getHeaders(), body: formData });
      setTimeout(() => fetchHistory(selectedUser.user_id, true), 1000);
    } catch (err) { alert("Nosūtīšanas kļūda!"); }
  };

  useEffect(() => { if (isLoggedIn) fetchUsers(); }, [isLoggedIn]);
  
  useEffect(() => {
    let interval;
    if (selectedUser) {
        interval = setInterval(() => fetchHistory(selectedUser.user_id, false), 3000);
    }
    return () => clearInterval(interval);
  }, [selectedUser]);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-4 text-white">
        <div className="bg-white/5 p-10 rounded-3xl border border-white/10 w-full max-w-md text-center shadow-[0_0_40px_rgba(212,175,55,0.1)]">
          <h1 className="text-3xl font-bold text-gold mb-8 tracking-widest">LEKTORS AI</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="PIEEJAS KODS" className="w-full bg-black/50 border border-white/10 text-white p-4 rounded-xl focus:border-gold outline-none text-center tracking-[0.5em]" />
            <button type="submit" className="w-full bg-gold hover:bg-yellow-500 text-black font-bold p-4 rounded-xl transition-all">IEIET PANELĪ</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full overflow-hidden bg-black text-white font-sans relative">
      
      {/* 🚀 GLOWING BACKGROUND ORBS FOR GLASSMORPHISM */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-yellow-500/10 rounded-full blur-[150px] pointer-events-none z-0" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none z-0" />

      {/* SIDEBAR */}
      <div className="w-80 flex-shrink-0 border-r border-white/10 bg-white/5 backdrop-blur-xl flex flex-col z-20 shadow-[4px_0_24px_rgba(0,0,0,0.5)] h-full">
        <div className="p-5 border-b border-white/5 flex flex-col gap-4">
          <div className="flex justify-between items-center">
             <h2 className="font-bold text-gold flex items-center gap-2 tracking-wide"><Users size={18}/> STUDENTI ({filteredUsers.length})</h2>
             <div className="flex gap-1">
                <button onClick={fetchUsers} className="p-2 hover:bg-white/10 rounded-lg text-gray-400"><RefreshCw size={16} className={loading ? 'animate-spin' : ''}/></button>
                <button onClick={logout} className="p-2 hover:bg-white/10 rounded-lg text-gray-500"><LogOut size={16}/></button>
             </div>
          </div>
          <input type="text" placeholder="Meklēt vārdu vai epastu..." value={searchTerm} onChange={handleSearch} className="w-full bg-black/50 border border-white/10 rounded-lg py-2 px-3 text-sm focus:border-gold outline-none text-gray-300"/>
          <button 
            onClick={() => setView(view === 'chat' ? 'dashboard' : 'chat')}
            className={`w-full py-2 rounded-lg text-xs font-bold transition-all border flex items-center justify-center gap-2 ${view === 'dashboard' ? 'bg-gold text-black border-gold' : 'bg-white/5 text-gold border-gold/30 hover:bg-gold/10'}`}
          >
            {view === 'chat' ? <Radio size={14}/> : <Users size={14}/>}
            {view === 'chat' ? 'SKATĪT ANALĪTIKU' : 'ATPAKAĻ UZ ČATU'}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {filteredUsers.map(u => (
            <div key={u.user_id} onClick={() => handleUserSelect(u)} className={`p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-all flex items-center gap-4 ${selectedUser?.user_id === u.user_id ? 'bg-gold/10 border-l-2 border-l-gold' : 'border-l-2 border-l-transparent'}`}>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center mb-1">
                    <h3 className="font-bold text-sm truncate flex items-center gap-2 text-white">
                        {u.first_name} {u.is_vip === 1 && <Star size={12} className="text-yellow-400 fill-current"/>}
                    </h3>
                    <div className="flex flex-col items-end">
                        <span className="text-[9px] text-gold font-bold">LVL {u.level || 1}</span>
                        <span className="text-[10px] text-gray-600">{u.status === 'active' ? 'Aktīvs' : 'Gaida'}</span>
                    </div>
                </div>
                <p className="text-xs text-gray-500 truncate flex justify-between">
                    <span>{u.email || '@'+(u.username || 'Nav')}</span>
                    <span className="text-blue-400 font-mono text-[9px]">{u.xp || 0} XP</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative z-10 p-4">
        {view === 'dashboard' ? (
          <div className="flex-1 overflow-y-auto p-6 space-y-8 animate-[fadeIn_0.5s_ease-out] relative z-10 custom-scrollbar">
             <h2 className="text-3xl font-black text-white italic tracking-tighter mb-8">KLASES ANALĪTIKA</h2>
             
             {/* Stats Grid */}
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/5 border border-white/10 p-6 rounded-[30px] backdrop-blur-xl">
                   <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Kopējais XP</p>
                   <p className="text-4xl font-black text-gold">{users.reduce((acc, u) => acc + (u.xp || 0), 0)}</p>
                </div>
                <div className="bg-white/5 border border-white/10 p-6 rounded-[30px] backdrop-blur-xl">
                   <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Vidējais Līmenis</p>
                   <p className="text-4xl font-black text-indigo-400">{(users.reduce((acc, u) => acc + (u.level || 1), 0) / (users.length || 1)).toFixed(1)}</p>
                </div>
                <div className="bg-white/5 border border-white/10 p-6 rounded-[30px] backdrop-blur-xl">
                   <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Aktīvi Studenti</p>
                   <p className="text-4xl font-black text-green-400">{users.filter(u => u.status === 'active').length}</p>
                </div>
             </div>

             {/* Progress Chart */}
             <div className="bg-white/5 border border-white/10 p-8 rounded-[40px] backdrop-blur-xl">
                <h3 className="font-bold text-xl mb-8 flex items-center gap-3">📊 LEKCIJU PROGRESU SADALĪJUMS</h3>
                <div className="space-y-6">
                   {[...Array(19)].map((_, i) => {
                       const count = users.filter(u => u.current_lesson === i).length;
                       const percent = (count / (users.length || 1)) * 100;
                       return (
                          <div key={i} className="flex items-center gap-4">
                             <span className="w-10 text-[10px] font-bold text-gray-500">L{i}</span>
                             <div className="flex-1 bg-white/5 rounded-full h-3 border border-white/5 overflow-hidden">
                                <div className="bg-gradient-to-r from-indigo-600 to-indigo-400 h-full rounded-full transition-all duration-1000" style={{ width: `${percent}%` }}></div>
                             </div>
                             <span className="w-8 text-right text-xs font-bold text-gray-300">{count}</span>
                          </div>
                       );
                   })}
                </div>
             </div>

             {/* Problem Students */}
             <div className="bg-red-500/5 border border-red-500/20 p-8 rounded-[40px] backdrop-blur-xl">
                <h3 className="font-bold text-xl text-red-400 mb-6 flex items-center gap-3">⚠️ KRITISKĀ ZONA (Neaktīvi 48h+)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   {users.filter(u => {
                      if (!u.last_activity) return true;
                      const last = new Date(u.last_activity);
                      const now = new Date();
                      return (now - last) > (48 * 60 * 60 * 1000);
                   }).slice(0, 6).map(u => (
                      <div key={u.user_id} className="p-4 bg-black/40 rounded-2xl border border-red-500/10 flex justify-between items-center">
                         <div>
                            <p className="font-bold text-sm text-white">{u.first_name}</p>
                            <p className="text-[10px] text-gray-500">Pēdējo reizi: {u.last_activity ? new Date(u.last_activity).toLocaleDateString() : 'Nekad'}</p>
                         </div>
                         <button onClick={() => { setSelectedUser(u); setView('chat'); }} className="text-[10px] font-bold text-red-400 hover:underline">Sazināties</button>
                      </div>
                   ))}
                </div>
             </div>
          </div>
        ) : selectedUser ? (
          <div className="flex-1 flex flex-col bg-black/40 backdrop-blur-2xl rounded-[30px] border border-white/10 shadow-2xl overflow-hidden relative">
            {/* ... existing chat UI ... */}
            <div className="p-5 border-b border-white/10 bg-gradient-to-r from-black/80 to-black/40 flex justify-between items-center shadow-lg backdrop-blur-md z-20">
               <div>
                 <h2 className="font-bold text-white text-xl flex items-center gap-3">
                     {selectedUser.first_name} {selectedUser.last_name}
                     {selectedUser.is_vip === 1 && <span className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/50 text-[10px] px-2 py-0.5 rounded-full flex items-center shadow-[0_0_10px_rgba(234,179,8,0.3)]">VIP PROFILS</span>}
                 </h2>
                 <p className="text-xs text-gray-400 mt-1 flex items-center gap-3">
                     <span>📞 <span className="text-gray-300">{selectedUser.phone || 'Nezināms'}</span></span>
                     <span>📧 <span className="text-gray-300">{selectedUser.email || 'Nezināms'}</span></span>
                     <span className="bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded border border-blue-500/20 font-bold">🎯 {selectedUser.xp || 0} XP</span>
                     <span className="bg-gold/10 text-gold px-2 py-0.5 rounded border border-gold/20 font-bold">🏆 LVL {selectedUser.level || 1}</span>
                 </p>
               </div>
               <div className="flex gap-3">
                   <button 
                      onClick={toggleVip}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all border ${
                          selectedUser.is_vip === 1 
                          ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50 shadow-[0_0_15px_rgba(234,179,8,0.2)]' 
                          : 'bg-gray-800 text-gray-400 border-gray-600 hover:bg-gray-700'
                      }`}
                   >
                      <Star size={14} className={selectedUser.is_vip === 1 ? 'fill-current' : ''} />
                      {selectedUser.is_vip === 1 ? 'VIP AKTĪVS' : 'Piešķirt VIP'}
                   </button>
                   <button 
                      onClick={toggleIntervention}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all border ${
                          selectedUser.intervention_mode === 1 
                          ? 'bg-red-500/20 text-red-400 border-red-500/50' 
                          : 'bg-green-500/20 text-green-400 border-green-500/50'
                      }`}
                   >
                      <Power size={14} />
                      {selectedUser.intervention_mode === 1 ? 'AI Izslēgts (Manuāli)' : 'AI Ieslēgts (Auto)'}
                   </button>
               </div>
            </div>

            {/* 🤖 AI STUDENT SUMMARY BOARD (WOW EFFECT) */}
            {(() => {
                const info = { age: '-', profession: '-', level: '-', goal: '-' };
                const aIdx = chatHistory.findIndex(m => m.content?.includes("Cik Tev ir gadu"));
                if(aIdx>-1 && chatHistory[aIdx+1]?.role === 'user') info.age = chatHistory[aIdx+1].content;
                
                const pIdx = chatHistory.findIndex(m => m.content?.includes("Kāda ir Tava profesija") || m.content?.includes("ar ko Tu nodarbojies"));
                if(pIdx>-1 && chatHistory[pIdx+1]?.role === 'user') info.profession = chatHistory[pIdx+1].content;
                
                const lIdx = chatHistory.findIndex(m => m.content?.includes("pieredzes līmenis"));
                if(lIdx>-1 && chatHistory[lIdx+1]?.role === 'user') info.level = chatHistory[lIdx+1].content;
                
                const gIdx = chatHistory.findIndex(m => m.content?.includes("vēlies AI kursā uzzināt"));
                if(gIdx>-1 && chatHistory[gIdx+1]?.role === 'user') info.goal = chatHistory[gIdx+1].content;

                // Rādīt tikai ja ir aizpildītas
                if(info.profession !== '-') {
                    return (
                        <div className="bg-gradient-to-r from-blue-900/10 to-indigo-900/10 border-b border-white/5 p-4 grid grid-cols-2 md:grid-cols-4 gap-4 z-10 relative">
                            <div className="bg-black/40 border border-white/10 p-3 rounded-2xl shadow-inner">
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-1">Vecums</span>
                                <span className="text-sm text-gray-200 font-medium truncate block">{info.age} gadi</span>
                            </div>
                            <div className="bg-black/40 border border-white/10 p-3 rounded-2xl shadow-inner">
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-1">Profesija</span>
                                <span className="text-sm text-gold font-medium truncate block shadow-[0_0_5px_rgba(212,175,55,0.2)]">{info.profession}</span>
                            </div>
                            <div className="bg-black/40 border border-white/10 p-3 rounded-2xl shadow-inner">
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-1">MI Līmenis</span>
                                <span className="text-sm text-indigo-300 font-medium truncate block">{info.level}</span>
                            </div>
                            <div className="bg-black/40 border border-white/10 p-3 rounded-2xl shadow-inner flex items-center group relative cursor-pointer">
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-1 absolute top-3">Mērķis</span>
                                <span className="text-sm text-gray-200 font-medium truncate block mt-4">{info.goal}</span>
                                <div className="hidden group-hover:block absolute top-[100%] left-0 w-64 bg-black border border-gold p-3 rounded-xl z-50 text-xs text-white shadow-2xl leading-relaxed mt-2 whitespace-pre-wrap">{info.goal}</div>
                            </div>
                        </div>
                    );
                }
                return null;
            })()}

            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 custom-scrollbar relative z-0">
               {chatHistory.map((msg, i) => (
                   <div key={i} className={`flex ${msg.role === 'admin' || msg.role === 'model' ? 'justify-start' : 'justify-end'} animate-[slideUp_0.3s_ease-out]`}>
                      <div className={`max-w-[75%] p-4 rounded-3xl text-sm shadow-xl ${
                          msg.role === 'user' 
                          ? 'bg-blue-600 text-white rounded-br-none' 
                          : msg.role === 'admin' 
                             ? 'bg-black border border-gold text-gold rounded-bl-none shadow-[0_0_15px_rgba(212,175,55,0.1)]' 
                             : 'bg-gray-800 border border-white/5 text-gray-200 rounded-bl-none'
                      }`}>
                         {msg.role === 'admin' && <span className="text-[9px] font-bold block mb-2 text-gold tracking-wider flex items-center gap-1"><Lock size={8}/> PASNIEDZĒJS</span>}
                         {msg.role === 'model' && <span className="text-[9px] font-bold block mb-2 text-indigo-400 tracking-wider flex items-center gap-1">🤖 LEKTORS AI</span>}
                         <div className="whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                      </div>
                   </div>
               ))}
               <div ref={chatEndRef} />
            </div>

            <form onSubmit={sendMessage} className="p-4 bg-black/60 backdrop-blur-3xl border-t border-white/10 flex flex-col gap-2 relative z-20">
               {selectedFile && (
                   <div className="flex items-center gap-2 p-2 bg-gradient-to-r from-blue-900/40 to-black rounded-lg border border-blue-500/30 text-sm text-gray-300 w-fit">
                       <Paperclip size={14} className="text-gold" />
                       <span className="truncate max-w-[200px]">{selectedFile.name}</span>
                       <button type="button" onClick={() => setSelectedFile(null)} className="hover:text-red-400 hover:rotate-90 ml-2 transition-all duration-300"><X size={14}/></button>
                   </div>
               )}
               <div className="flex gap-3 items-center">
                   <button type="button" onClick={() => fileInputRef.current.click()} className="group p-4 bg-white/5 rounded-2xl hover:bg-white/10 border border-transparent hover:border-white/10 text-gray-400 hover:text-gold transition-all duration-300 shadow-inner">
                       <ImageIcon size={20} className="group-hover:scale-110 transition-transform"/>
                   </button>
                   <input type="file" ref={fileInputRef} className="hidden" onChange={(e) => setSelectedFile(e.target.files[0])} />
                   
                   <input 
                       type="text" value={message} onChange={(e) => setMessage(e.target.value)}
                       placeholder="Sagatavot atbildi studentam..." 
                       className="flex-1 bg-black/50 border border-white/10 text-white p-4 rounded-2xl focus:border-gold outline-none transition-all duration-300 w-full placeholder-gray-600 shadow-inner hover:bg-black/70"
                   />
                   <button type="submit" disabled={!message.trim() && !selectedFile} className="group bg-gold hover:bg-yellow-400 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] text-black p-4 rounded-2xl disabled:opacity-30 disabled:hover:shadow-none transition-all duration-300">
                       <Send size={20} className="ml-0.5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"/>
                   </button>
               </div>
            </form>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-600">
             <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mb-6">
                <Users size={40}/>
             </div>
             <h3 className="text-xl font-bold text-white mb-2">Gaidīšanas Režīms</h3>
             <p className="text-sm">Izvēlies studentu no saraksta, lai pārvaldītu viņa datus vai pārņemtu saraksti.</p>
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes slideUp {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
        }
      `}} />
    </div>
  );
}

export default AppAdmin;